import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Grid, TextField, Autocomplete, Typography, Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, IconButton, MenuItem, List, ListItem, ListItemText, Divider, ListItemButton, ListSubheader, Collapse, useMediaQuery, useTheme, Card, CardContent, Drawer } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import apiService from '../../services/apiService';
import { getCurrentUser } from '../../services/authService';
import { format, parseISO } from 'date-fns';
import BillPrintDialog from './BillPrintDialog';
import AddProductDialog from './AddProductDialog';

const filter = createFilterOptions();

const WALKING_CUSTOMER_ID = 1; // Using a constant for the "Walking Customer"

const emptyForm = {
  salesDetailId: null,
  product: null,
  productInput: '',
  quantity: '',
  sellingPrice: '',
  price: '',
};

const QuickBill = () => {
  const [products, setProducts] = useState([]);
  const [allBills, setAllBills] = useState([]); // Store all fetched bills
  const [selectedBillId, setSelectedBillId] = useState(null);
  const [salesList, setSalesList] = useState([]);
  const [completedExpanded, setCompletedExpanded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [form, setForm] = useState(emptyForm);

  const [payment, setPayment] = useState({ received: '', balance: '0.00' });
  const [saving, setSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPrintDialogOpen, setPrintDialogOpen] = useState(false);
  const [salesDetailsForPrint, setSalesDetailsForPrint] = useState([]);
  const productInputRef = useRef(null);
  const sellingPriceInputRef = useRef(null);
  const quantityInputRef = useRef(null);

  // State for Add Product Dialog
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Check for Super User role
  const user = getCurrentUser();
  const rolesRaw = user?.roles || user?.authorities || user?.permissions || user?.role || [];
  const roles = Array.isArray(rolesRaw)
    ? rolesRaw.map(r => (typeof r === 'string' ? r.toLowerCase() : String(r?.authority || r?.name || '').toLowerCase()))
    : String(rolesRaw).toLowerCase();
  const isSuperUser = roles.includes('super_user') || roles.includes('role_super_user');

  const loadProducts = useCallback(async () => {
    try {
      const resp = await apiService.products.list({ page: 0, size: 1000 });
      setProducts(resp.data?.content || []);
    } catch (e) {
      setProducts([]);
    }
  }, []);

  const loadActiveBills = useCallback(async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      // Fetch ALL Quick Bills for today (both paid and unpaid)
      const resp = await apiService.collections.getActiveBillsToday({
        date: today,
        customerId: WALKING_CUSTOMER_ID,
        billTypeFlag: 'Q'
      });
      setAllBills(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      setAllBills([]);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadActiveBills();
  }, [loadProducts, loadActiveBills]);

  // Derived state for splitting the list
  const pendingBills = useMemo(() => allBills.filter(b => b.isPaid !== 'Y'), [allBills]);
  const completedBills = useMemo(() => allBills.filter(b => b.isPaid === 'Y'), [allBills]);

  const selectedBill = useMemo(() => {
    return allBills.find(b => b.billSummaryId === selectedBillId);
  }, [allBills, selectedBillId]);

  const isBillPaid = selectedBill?.isPaid === 'Y';

  useEffect(() => {
    const fetchDetails = async () => {
      setForm(emptyForm);
      if (selectedBillId && !String(selectedBillId).startsWith('new-')) {
        try {
          const response = await apiService.salesDetails.getSalesByBillSummary(selectedBillId);
          setSalesList(response.data || []);
        } catch (error) {
          // If fetch fails (e.g., bill was deleted by backend), clear list
          setSalesList([]);
          // If the bill is no longer in the active list, deselect it
          if (!allBills.find(b => b.billSummaryId === selectedBillId)) {
             setSelectedBillId(null);
          }
        }
      } else if (String(selectedBillId).startsWith('new-')) {
        setSalesList([]);
      }
    };
    fetchDetails();
  }, [selectedBillId, allBills]); // Added allBills dependency to react to list updates

  const handleNewBill = () => {
    const newBillId = `new-${Date.now()}`;
    const newBill = {
      billSummaryId: newBillId,
      manualBillNo: `New Bill ${pendingBills.filter(b => String(b.billSummaryId).startsWith('new-')).length + 1}`,
      billAmount: 0,
      isNew: true,
      billTypeFlag: 'Q',
      isPaid: 'N',
      updateTimestamp: new Date().toISOString(),
      updateUser: user?.username
    };
    setAllBills(prev => [newBill, ...prev]);
    setSelectedBillId(newBillId);
  };

  const handleBillSelect = (billId) => {
    setForm(emptyForm);
    setSelectedBillId(billId);
  };

  const onProductChange = (_e, val) => {
    if (val?.isAddNew) {
        setAddProductDialogOpen(true);
        return;
    }
    setForm(prev => ({
      ...prev,
      product: val || null,
      sellingPrice: val ? val.sellingPrice : '',
      price: '', // Reset price when product changes, wait for quantity
    }));
    if (val) {
      sellingPriceInputRef.current?.focus();
    }
  };

  const handleSaveNewProduct = async (newProduct) => {
    await loadProducts();
    setForm(prev => ({
        ...prev,
        product: newProduct,
        sellingPrice: newProduct.sellingPrice || '',
    }));
    setAddProductDialogOpen(false);
    requestAnimationFrame(() => {
        sellingPriceInputRef.current?.focus();
    });
  };

  const handleFormChange = (name, value) => {
    setForm(prev => {
      const newForm = { ...prev, [name]: value };
      const quantity = Number(newForm.quantity) || 0;
      const sellingPrice = Number(newForm.sellingPrice) || 0;

      if (name === 'quantity' || name === 'sellingPrice') {
        newForm.price = quantity > 0 ? String(sellingPrice * quantity) : '';
      }
      return newForm;
    });
  };

  const validate = () => {
    if (!form.product) {
      alert('Please select a product.');
      return false;
    }
    if (Number(form.quantity || 0) <= 0) {
      alert('Quantity must be greater than 0.');
      return false;
    }
    if (Number(form.price || 0) <= 0) {
      alert('Price must be greater than 0.');
      return false;
    }
    return true;
  };

  const saveItem = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        salesDetailId: form.salesDetailId,
        productId: form.product.productId,
        customerId: WALKING_CUSTOMER_ID,
        quantity: form.quantity,
        salesPrice: form.sellingPrice,
        price: form.price,
        billTypeFlag: 'Q'
      };

      if (selectedBillId && !String(selectedBillId).startsWith('new-')) {
        payload.billSummaryId = selectedBillId;
      }

      const response = await apiService.quickBill.addItem(payload);
      const { salesDetails, billSummary } = response.data;

      setSalesList(salesDetails || []);

      // Update the allBills list
      if (!payload.billSummaryId) { // This was a new bill
        setAllBills(prev => [billSummary, ...prev.filter(b => b.billSummaryId !== selectedBillId)]);
        setSelectedBillId(billSummary.billSummaryId);
      } else { // This was an existing bill
        setAllBills(prev => prev.map(b =>
          b.billSummaryId === billSummary.billSummaryId ? billSummary : b
        ));
      }

      setForm(emptyForm);
    } catch (error) {
      alert('Failed to save item.');
    } finally {
      setSaving(false);
      productInputRef.current?.focus();
    }
  };

  const handleEditItem = (item) => {
    const product = products.find(p => p.productId === item.productId);
    setForm({
      salesDetailId: item.salesDetailId,
      product: product || null,
      productInput: product ? `${product.productName} - ${product.productNameTamil}` : '',
      quantity: item.quantity,
      sellingPrice: item.salesPrice,
      price: item.price,
    });
  };

  const handleCancelEdit = () => {
    setForm(emptyForm);
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      // Backend handles bill summary deletion if it's the last item
      await apiService.salesDetails.delete(item.salesDetailId);

      // Refresh the active bills list.
      // If the bill was deleted by the backend, it will disappear from this list.
      await loadActiveBills();

      // We also need to refresh the sales list for the current view if the bill still exists
      if (selectedBillId) {
         try {
            const response = await apiService.salesDetails.getSalesByBillSummary(selectedBillId);
            setSalesList(response.data || []);
         } catch (e) {
            // If fetching details fails (likely because bill is gone), clear the view
            setSalesList([]);
            setSelectedBillId(null);
         }
      }
    } catch (error) {
      alert('Failed to delete item.');
    }
  };

  const totals = useMemo(() => {
    const billAmount = salesList.reduce((acc, item) => acc + (Number(item.price) || 0), 0);
    const received = Number(payment.received || 0);
    const balance = billAmount - received;
    return { billAmount, received, balance };
  }, [salesList, payment.received]);

  const finalizePayment = async () => {
    if (!selectedBillId || Number(totals.received) <= 0) {
      alert('Please enter a valid received amount.');
      return;
    }
    setSaving(true);
    try {
      await apiService.customerBill.receivePayment({
        billSummaryId: selectedBillId,
        receivedAmount: totals.received,
        billTypeFlag: 'Q'
      });

      // Refresh the list to move the bill to "Completed"
      await loadActiveBills();

      // Expand completed section to show the moved bill
      setCompletedExpanded(true);

      setPayment({ received: '', balance: '0.00' });

    } catch (error) {
      alert('Failed to finalize payment.');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async () => {
    if (!selectedBillId) return;
    setIsPrinting(true);
    try {
      const response = await apiService.salesDetails.getSalesByBillSummary(selectedBillId);
      setSalesDetailsForPrint(response.data || []);
      setPrintDialogOpen(true);
    } catch (error) {
      alert('Could not fetch bill details for printing.');
    } finally {
      setIsPrinting(false);
    }
  };

  const isAddItemDisabled = isBillPaid || saving || !form.product || !form.quantity || Number(form.quantity) <= 0 || Number(form.price) <= 0;

  // Render Logic for Mobile vs Desktop
  const showBillList = !isMobile || !selectedBillId;
  const showBillDetails = !isMobile || selectedBillId;

  const renderBillList = (isDrawer = false) => (
    <Paper sx={{ width: isDrawer ? '100%' : (isMobile ? '100%' : 250), display: 'flex', flexDirection: 'column', flexShrink: 0, height: isMobile && !isDrawer ? '100%' : 'auto', boxShadow: isDrawer ? 0 : 1 }}>
      <Box sx={{ p: 1, borderBottom: '1px solid #eee', display: 'flex', gap: 1 }}>
        <Button
          fullWidth
          variant={selectedBillId === null ? "contained" : "outlined"}
          startIcon={<AddIcon />}
          onClick={() => {
            handleNewBill();
            if (isDrawer) setMobileOpen(false);
          }}
        >
          New Bill
        </Button>
        {isDrawer && (
          <IconButton onClick={() => setMobileOpen(false)}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <List sx={{ overflowY: 'auto', flex: 1 }} dense>
        {/* Active Bills Section */}
        <ListSubheader sx={{ bgcolor: '#e3f2fd', lineHeight: '36px' }}>Active Bills</ListSubheader>
        {pendingBills.map(bill => (
          <ListItemButton
            key={bill.billSummaryId}
            selected={selectedBillId === bill.billSummaryId}
            onClick={() => {
              handleBillSelect(bill.billSummaryId);
              if (isDrawer) setMobileOpen(false);
            }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {bill.manualBillNo || `Bill #${bill.billSummaryId}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {bill.updateTimestamp ? format(parseISO(bill.updateTimestamp), 'HH:mm') : ''}
                  </Typography>
                </Box>
              }
              secondary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total: {(Number(bill.billAmount) || 0).toFixed(2)}
                  </Typography>
                  {isSuperUser && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {bill.updateUser || bill.createdBy || ''}
                    </Typography>
                  )}
                </Box>
              }
            />
          </ListItemButton>
        ))}

        {/* Completed Bills Section */}
        {completedBills.length > 0 && (
          <>
            <Divider />
            <ListItemButton onClick={() => setCompletedExpanded(!completedExpanded)} sx={{ bgcolor: '#e8f5e9' }}>
              <ListItemText primary="Completed Today" />
              {completedExpanded ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={completedExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {completedBills.map(bill => (
                  <ListItemButton
                    key={bill.billSummaryId}
                    selected={selectedBillId === bill.billSummaryId}
                    onClick={() => {
                      handleBillSelect(bill.billSummaryId);
                      if (isDrawer) setMobileOpen(false);
                    }}
                    sx={{ pl: 4, opacity: 0.8 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            {bill.manualBillNo}
                            <CheckCircleIcon sx={{ fontSize: 16, ml: 1, color: 'success.main' }} />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {bill.updateTimestamp ? format(parseISO(bill.updateTimestamp), 'HH:mm') : ''}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Total: {(Number(bill.billAmount) || 0).toFixed(2)}
                          </Typography>
                          {isSuperUser && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {bill.updateUser || bill.createdBy || ''}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </List>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 24px)', p: 2, flexDirection: isMobile ? 'column' : 'row' }}>

      {/* Left Panel: Active & Completed Bills */}
      {showBillList && renderBillList()}

      {/* Right Panel: Bill Details */}
      {showBillDetails && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0, height: isMobile ? '100%' : 'auto' }}>

          {/* Mobile Back Button */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pl: 8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={() => setSelectedBillId(null)} size="small">
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 700 }}>
                  {selectedBill?.manualBillNo || 'New Bill'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" size="small" onClick={() => setMobileOpen(true)} sx={{ minWidth: 'auto', px: 1 }}>
                  Bills
                </Button>
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleNewBill} sx={{ minWidth: 'auto', px: 1 }}>
                  New
                </Button>
              </Box>
            </Box>
          )}

          {/* Item Entry Form - Disabled if Paid */}
          <Paper sx={{ p: { xs: 1.5, md: 2 }, flexShrink: 0, opacity: isBillPaid ? 0.6 : 1, pointerEvents: isBillPaid ? 'none' : 'auto' }}>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: { xs: 1.5, md: 2 }, alignItems: isMobile ? 'stretch' : 'center' }}>
              <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 40%' }}>
                <Autocomplete
                  options={products}
                  getOptionLabel={(o) => {
                    if (!o) return '';
                    if (o.isAddNew) return o.productName;
                    return `${o.productName} - ${o.productNameTamil}`;
                  }}
                  value={form.product}
                  onChange={onProductChange}
                  inputValue={form.productInput}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    const { inputValue } = params;
                    const isExisting = options.some((option) => inputValue === option.productName);
                    if (inputValue !== '' && !isExisting) {
                        filtered.push({
                            productId: 'ADD_NEW', productName: `Add "${inputValue}"`, isAddNew: true
                        });
                    }
                    return filtered;
                  }}
                  onInputChange={(_e, v) => handleFormChange('productInput', v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Product"
                      size="small"
                      autoFocus={!isBillPaid && !isMobile}
                      inputRef={productInputRef}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const firstMatch = products.find(p =>
                            `${p.productName} - ${p.productNameTamil}`.toLowerCase().includes(form.productInput.toLowerCase())
                          );
                          if (firstMatch) {
                            onProductChange(null, firstMatch);
                          }
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    if (option.isAddNew) {
                        return (
                            <li key={key} {...otherProps} style={{ borderTop: '1px solid #eee', color: '#1976d2', fontWeight: 600 }}>
                                + {option.productName}
                            </li>
                        );
                    }
                    return (
                        <li key={key} {...otherProps}>
                            {`${option.productName} - ${option.productNameTamil}`}
                        </li>
                    );
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flex: isMobile ? '1 1 100%' : '0 1 auto' }}>
                <TextField
                  label="Selling Price"
                  name="sellingPrice"
                  value={form.sellingPrice}
                  onChange={(e) => handleFormChange('sellingPrice', e.target.value)}
                  type="number"
                  size="small"
                  fullWidth
                  sx={{ flex: 1 }}
                  inputRef={sellingPriceInputRef}
                  onKeyDown={(e) => { if (e.key === 'Enter') quantityInputRef.current?.focus(); }}
                />
                <TextField
                  label="Quantity"
                  name="quantity"
                  value={form.quantity}
                  onChange={(e) => handleFormChange('quantity', e.target.value)}
                  type="number"
                  size="small"
                  fullWidth
                  sx={{ flex: 1 }}
                  inputRef={quantityInputRef}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveItem(); }}
                />
                <TextField
                  label="Price"
                  value={form.price}
                  InputProps={{ readOnly: true }}
                  size="small"
                  fullWidth
                  sx={{ flex: 1 }}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveItem(); }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, minWidth: isMobile ? '100%' : '120px' }}>
                  <Button fullWidth variant="contained" startIcon={<SaveIcon />} onClick={saveItem} disabled={isAddItemDisabled}>
                    {form.salesDetailId ? 'Update' : 'Add'}
                  </Button>
                  {form.salesDetailId && (
                    <IconButton onClick={handleCancelEdit} color="secondary" size="small" sx={{ border: '1px solid', borderRadius: 1 }}>
                      <CancelIcon />
                    </IconButton>
                  )}
              </Box>
            </Box>
          </Paper>

          {/* Sales List */}
          {isMobile ? (
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pb: 1 }}>
              {salesList.map((item, index) => {
                const product = products.find(p => p.productId === item.productId);
                return (
                  <Paper key={item.salesDetailId} sx={{ p: 0.75, mb: 0.5, mx: 0.5, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                        {index + 1}. {product?.productName}
                        {product?.productNameTamil && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({product.productNameTamil})
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                        {Number(item.salesPrice).toFixed(2)} x {item.quantity} = <b>{Number(item.price).toFixed(2)}</b>
                      </Typography>
                    </Box>

                    {!isBillPaid && (
                      <Box sx={{ display: 'flex', flexShrink: 0, ml: 0.5 }}>
                        <IconButton size="small" onClick={() => handleEditItem(item)} sx={{ p: 0.5 }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteItem(item)} sx={{ p: 0.5 }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
              {salesList.length === 0 && (
                <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 4 }}>
                  No items added yet.
                </Typography>
              )}
            </Box>
          ) : (
          <TableContainer component={Paper} sx={{ flex: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Price</TableCell>
                  {!isBillPaid && <TableCell align="center">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {salesList.map((item, index) => {
                  const product = products.find(p => p.productId === item.productId);
                  return (
                    <TableRow key={item.salesDetailId}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{product?.productNameTamil || product?.productName || ''}</TableCell>
                      <TableCell align="right">{Number(item.salesPrice).toFixed(2)}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{Number(item.price).toFixed(2)}</TableCell>
                      {!isBillPaid && (
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleEditItem(item)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteItem(item)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          {/* Payment Footer */}
          {selectedBillId && (
            <Paper sx={{ p: { xs: 1, md: 2 }, flexShrink: 0 }}>
              <Grid container spacing={2} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Grid item xs={12} md>
                  <Typography variant="h5">Total: {totals.billAmount.toFixed(2)}</Typography>
                </Grid>

                {!isBillPaid ? (
                  <>
                    <Grid item xs={6} md={3}>
                      <TextField
                        label="Received Amount"
                        value={payment.received}
                        onChange={(e) => setPayment(p => ({...p, received: e.target.value}))}
                        type="number"
                        fullWidth
                        onKeyDown={(e) => { if (e.key === 'Enter') finalizePayment(); }}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Typography variant="h6" color="error">Balance: {totals.balance.toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6} md={1.5}>
                      <Button fullWidth variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} disabled={isPrinting}>
                        Print
                      </Button>
                    </Grid>
                    <Grid item xs={6} md={1.5}>
                      <Button fullWidth variant="contained" color="success" onClick={finalizePayment} disabled={saving}>
                        Finalize
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={6} md={3}>
                      <Typography variant="h6" color="success.main">
                        Received: {(Number(selectedBill?.receivedAmount) || 0).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3} sx={{ textAlign: 'right' }}>
                      <Button fullWidth variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} disabled={isPrinting}>
                        Reprint Bill
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Mobile View */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                {!isBillPaid ? (
                  <>
                    <Box>
                      <Typography variant="caption" display="block" sx={{ fontWeight: 700 }}>Tot: {totals.billAmount.toFixed(2)}</Typography>
                      <Typography variant="caption" display="block" color="error" sx={{ fontWeight: 700 }}>Bal: {totals.balance.toFixed(2)}</Typography>
                    </Box>
                    <TextField
                      placeholder="Rcvd"
                      value={payment.received}
                      onChange={(e) => setPayment(p => ({...p, received: e.target.value}))}
                      type="number"
                      size="small"
                      sx={{ width: 80, '& .MuiInputBase-input': { p: '4px 8px' } }}
                      onKeyDown={(e) => { if (e.key === 'Enter') finalizePayment(); }}
                    />
                    <IconButton color="success" onClick={finalizePayment} disabled={saving} sx={{ border: '1px solid', borderRadius: 1, p: 0.5, borderColor: 'divider' }}>
                      <CheckCircleIcon />
                    </IconButton>
                    <IconButton color="primary" onClick={handlePrint} disabled={isPrinting} sx={{ border: '1px solid', borderRadius: 1, p: 0.5, borderColor: 'divider' }}>
                      <PrintIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                     <Box>
                        <Typography variant="caption" display="block" sx={{ fontWeight: 700 }}>Total: {totals.billAmount.toFixed(2)}</Typography>
                        <Typography variant="caption" display="block" color="success.main" sx={{ fontWeight: 700 }}>Rcvd: {(Number(selectedBill?.receivedAmount) || 0).toFixed(2)}</Typography>
                     </Box>
                     <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={handlePrint} disabled={isPrinting}>
                        Reprint
                     </Button>
                  </>
                )}
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {/* Print Dialog */}
      {selectedBill && (
        <BillPrintDialog
          open={isPrintDialogOpen}
          onClose={() => setPrintDialogOpen(false)}
          bill={{...selectedBill, billTypeFlag: 'Q'}}
          salesDetails={salesDetailsForPrint}
        />
      )}

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { width: '85%', maxWidth: 300 } }}
      >
        {renderBillList(true)}
      </Drawer>

      <AddProductDialog
        open={addProductDialogOpen}
        onClose={() => setAddProductDialogOpen(false)}
        onSaveSuccess={handleSaveNewProduct}
      />
    </Box>
  );
};

export default QuickBill;
